import {isEmpty} from 'lodash';
import {useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {
    ConversationSummaryAccordion, DataWrapper, IbmMessageHistoryButton, InformationModal,
    TextChatMessageList, TranslationLanguageSelector, UserParticipantIds
} from 'components';
import {ACTIVITY_NAME_TRANSLATE} from 'constants/session';
import {usePrevious} from 'hooks';
import {AnyParticipant, ConversationSummaryData} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale/translation-keys';
import {TextChatMessage} from 'types';

type Props= {
    open: boolean;
    isLoading?: boolean;
    isError?: boolean;
    conversationSummary?: ConversationSummaryData;
    sessionId?: string;
    activityId?: string;
    activityName?: string;
    userParticipantIds: UserParticipantIds;
    participants: AnyParticipant[];
    localParticipantId?: string;
    hideConversationSummary?: boolean;
    messages?: TextChatMessage[];
    selectedLanguage?: string;
    availableLanguages?: string[];
    integrations?: {
        ibm?: {
            sessionHistoryKey: string;
        }
    }
    onLanguageChange?: (language: string) => void;
    onClose: (params?: {ibm?: {sessionHistoryKey: string}}) => void;
}

export const MessagesModal: React.FC<Props> = ({
    open, isError, isLoading, messages, integrations, sessionId, availableLanguages, participants,
    activityId, selectedLanguage = null, conversationSummary, hideConversationSummary,
    localParticipantId, userParticipantIds, activityName, onClose, onLanguageChange,
}) => {
    const {t} = useTranslation();
    const prevOpen = usePrevious(open);
    const title = useMemo(() => sessionId ? `${t(ACTIVITY_NAME_TRANSLATE[activityName])}: ${sessionId}` : '', [sessionId, activityName, t]);

    const handleLanguageChange = useCallback((lang: string) => {
        onLanguageChange?.(lang);
    }, [onLanguageChange]);

    useEffect(() => {
        if (!open && prevOpen) {
            handleLanguageChange(null);
        }
    }, [handleLanguageChange, open, prevOpen]);

    return (
        <InformationModal
            title={title}
            maxWidth='sm'
            fullWidth
            open={open}
            onClose={onClose}
        >
            <DataWrapper isLoading={isLoading} isError={isError} errorClassName='tw-text-xl'>
                {!isEmpty(availableLanguages) &&
                    <div className='tw-mb-4'>
                        <TranslationLanguageSelector
                            placeholder={t(TRANSLATION_KEYS.COMPONENTS.MESSAGES_MODAL.TRANSLATION_LANGUAGE)}
                            value={selectedLanguage}
                            include={availableLanguages}
                            className='tw-w-48'
                            onChange={handleLanguageChange}
                        />
                    </div>}
                {!isEmpty(messages) && !hideConversationSummary &&
                    <ConversationSummaryAccordion data={conversationSummary} sessionId={sessionId} activityId={activityId} />}
                <div className='vs-session-text-chat tw-pr-4'>
                    {integrations?.ibm?.sessionHistoryKey && (
                        <IbmMessageHistoryButton
                            className='tw-mb-4'
                            onClick={() => onClose({ibm: integrations.ibm})}
                        />
                    )}
                    <TextChatMessageList
                        messages={messages}
                        participants={participants}
                        localParticipantId={localParticipantId}
                        userParticipantIds={userParticipantIds}
                        downloadFile={() => null}
                        messagesEndRef={null}
                        downloadingFiles={[]}
                        typingRemoteParticipantIds={[]}
                        hideEmptyMessagesLabel={!!integrations?.ibm?.sessionHistoryKey}
                        downloadFileDisabled
                    />
                </div>
            </DataWrapper>
        </InformationModal>
    );
};